import { useState, useEffect } from "react";
import { AnimatedSection } from "@/components/AnimatedSection";
import { FloatingCard } from "@/components/FloatingCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Link } from "wouter";
import { ArrowLeft, Mail, Lock, User, Wand2, CheckCircle2, AlertCircle } from "lucide-react";
import linaImage from "@assets/lina desk_1764231656647.png";
import { useAuth } from "@/hooks/useAuth";
import { apiRequest } from "@/lib/queryClient";

type FormState = "idle" | "loading" | "success" | "error";

export default function LoginPage() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  // Login form
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginState, setLoginState] = useState<FormState>("idle");
  const [loginError, setLoginError] = useState("");

  // Register form
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [regConfirm, setRegConfirm] = useState("");
  const [regState, setRegState] = useState<FormState>("idle");
  const [regError, setRegError] = useState("");
  const [regSuccess, setRegSuccess] = useState(false);

  // Magic link
  const [magicEmail, setMagicEmail] = useState("");
  const [magicState, setMagicState] = useState<FormState>("idle");
  const [magicSent, setMagicSent] = useState(false);

  // Forgot password
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotState, setForgotState] = useState<FormState>("idle");
  const [forgotSent, setForgotSent] = useState(false);

  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      window.location.href = "/";
    }
  }, [isAuthenticated, authLoading]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-card to-background">
        <div className="text-center space-y-4">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="text-primary font-medium">Lade...</p>
        </div>
      </div>
    );
  }

  if (isAuthenticated) return null;

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginState("loading");
    setLoginError("");
    try {
      await apiRequest("POST", "/api/auth/login", {
        email: loginEmail,
        password: loginPassword,
      });
      window.location.href = "/";
    } catch (err: any) {
      setLoginState("error");
      setLoginError(err.data?.error || "Anmeldung fehlgeschlagen");
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setRegError("");

    if (regPassword !== regConfirm) {
      setRegError("Passwörter stimmen nicht überein");
      return;
    }

    setRegState("loading");
    try {
      await apiRequest("POST", "/api/auth/register", {
        email: regEmail,
        password: regPassword,
        firstName,
        lastName,
      });
      setRegState("success");
      setRegSuccess(true);
    } catch (err: any) {
      setRegState("error");
      setRegError(err.data?.error || "Registrierung fehlgeschlagen");
    }
  };

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setMagicState("loading");
    try {
      await apiRequest("POST", "/api/auth/magic-link", {
        email: magicEmail,
      });
      setMagicState("success");
      setMagicSent(true);
    } catch {
      setMagicState("error");
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotState("loading");
    try {
      await apiRequest("POST", "/api/auth/forgot-password", {
        email: forgotEmail,
      });
      setForgotState("success");
      setForgotSent(true);
    } catch {
      setForgotState("error");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-card to-background">
      <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container flex h-16 items-center justify-between px-6 lg:px-8">
          <AnimatedSection direction="left" className="flex items-center space-x-2">
            <div className="w-8 h-8 rounded-md bg-primary flex items-center justify-center">
              <span className="text-primary-foreground font-bold text-lg font-playfair">A</span>
            </div>
            <span className="text-xl font-bold text-primary font-playfair">Social Media A-Team</span>
          </AnimatedSection>
          <AnimatedSection direction="right">
            <Button variant="ghost" size="sm" asChild className="gap-2">
              <Link href="/">
                <ArrowLeft className="h-4 w-4" />
                Zurück
              </Link>
            </Button>
          </AnimatedSection>
        </div>
      </header>

      <main className="container px-6 lg:px-8 py-12 lg:py-20">
        <div className="max-w-md mx-auto">
          <AnimatedSection direction="up">
            <FloatingCard>
              <CardContent className="pt-8 pb-8 space-y-6">
                {/* Avatar + Welcome */}
                <div className="flex flex-col items-center text-center space-y-4">
                  <Avatar className="w-24 h-24 border-4 border-purple-300 shadow-lg">
                    <AvatarImage src={linaImage} alt="Lina" />
                    <AvatarFallback className="bg-purple-100 text-purple-700 text-2xl font-semibold">L</AvatarFallback>
                  </Avatar>
                  <div className="space-y-1">
                    <h1 className="text-2xl font-bold font-playfair">Willkommen!</h1>
                    <p className="text-muted-foreground">Melden Sie sich an oder erstellen Sie ein Konto</p>
                  </div>
                </div>

                <div className="border-l-4 border-purple-300 pl-4 py-2">
                  <p className="italic text-muted-foreground">
                    "Guter Content braucht ein gutes Team. Meins steht bereit."
                  </p>
                  <p className="text-sm text-purple-600 dark:text-purple-400 font-medium mt-1">Lina vom A-Team</p>
                </div>

                {/* Tabs: Login / Register — deep-linkable via ?tab=register (direct-start CTA) */}
                <Tabs defaultValue={new URLSearchParams(window.location.search).get("tab") === "register" ? "register" : "login"} className="space-y-4">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="login">Anmelden</TabsTrigger>
                    <TabsTrigger value="register">Registrieren</TabsTrigger>
                  </TabsList>

                  {/* Login Tab */}
                  <TabsContent value="login" className="space-y-4">
                    {showForgot ? (
                      <div className="space-y-4">
                        <h3 className="text-lg font-semibold">Passwort zurücksetzen</h3>
                        {forgotSent ? (
                          <div className="flex items-start gap-3 p-4 rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800">
                            <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                            <p className="text-sm text-green-700 dark:text-green-300">
                              Falls diese E-Mail registriert ist, wurde ein Link zum Zurücksetzen gesendet. Bitte prüfen Sie Ihr Postfach.
                            </p>
                          </div>
                        ) : (
                          <form onSubmit={handleForgotPassword} className="space-y-3">
                            <div className="space-y-2">
                              <Label htmlFor="forgot-email">E-Mail</Label>
                              <div className="relative">
                                <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                                <Input
                                  id="forgot-email"
                                  type="email"
                                  placeholder="name@hotel.de"
                                  className="pl-9"
                                  value={forgotEmail}
                                  onChange={(e) => setForgotEmail(e.target.value)}
                                  required
                                />
                              </div>
                            </div>
                            <Button type="submit" className="w-full" disabled={forgotState === "loading"}>
                              {forgotState === "loading" ? "Sende..." : "Link senden"}
                            </Button>
                          </form>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="w-full"
                          onClick={() => { setShowForgot(false); setForgotSent(false); }}
                        >
                          Zurück zur Anmeldung
                        </Button>
                      </div>
                    ) : (
                      <>
                        <form onSubmit={handleLogin} className="space-y-3">
                          <div className="space-y-2">
                            <Label htmlFor="login-email">E-Mail</Label>
                            <div className="relative">
                              <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                              <Input
                                id="login-email"
                                type="email"
                                placeholder="name@hotel.de"
                                className="pl-9"
                                value={loginEmail}
                                onChange={(e) => setLoginEmail(e.target.value)}
                                required
                              />
                            </div>
                          </div>
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <Label htmlFor="login-password">Passwort</Label>
                              <button
                                type="button"
                                className="text-xs text-primary hover:underline"
                                onClick={() => setShowForgot(true)}
                              >
                                Passwort vergessen?
                              </button>
                            </div>
                            <div className="relative">
                              <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                              <Input
                                id="login-password"
                                type="password"
                                placeholder="Ihr Passwort"
                                className="pl-9"
                                value={loginPassword}
                                onChange={(e) => setLoginPassword(e.target.value)}
                                required
                              />
                            </div>
                          </div>

                          {loginError && (
                            <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
                              <AlertCircle className="h-4 w-4 flex-shrink-0" />
                              {loginError}
                            </div>
                          )}

                          <Button type="submit" className="w-full" disabled={loginState === "loading"}>
                            {loginState === "loading" ? "Anmelden..." : "Anmelden"}
                          </Button>
                        </form>

                        <div className="relative">
                          <div className="absolute inset-0 flex items-center">
                            <Separator />
                          </div>
                          <div className="relative flex justify-center text-xs uppercase">
                            <span className="bg-card px-2 text-muted-foreground">oder</span>
                          </div>
                        </div>

                        {magicSent ? (
                          <div className="flex items-start gap-3 p-4 rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800">
                            <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                            <div className="text-sm text-green-700 dark:text-green-300">
                              <p className="font-medium">Magic Link gesendet!</p>
                              <p>Bitte prüfen Sie Ihr E-Mail-Postfach und klicken Sie auf den Link zum Anmelden.</p>
                            </div>
                          </div>
                        ) : (
                          <form onSubmit={handleMagicLink} className="space-y-3">
                            <div className="space-y-2">
                              <Label htmlFor="magic-email">Magic Link per E-Mail</Label>
                              <div className="relative">
                                <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                                <Input
                                  id="magic-email"
                                  type="email"
                                  placeholder="name@hotel.de"
                                  className="pl-9"
                                  value={magicEmail}
                                  onChange={(e) => setMagicEmail(e.target.value)}
                                  required
                                />
                              </div>
                            </div>
                            <Button type="submit" variant="outline" className="w-full gap-2" disabled={magicState === "loading"}>
                              <Wand2 className="h-4 w-4" />
                              {magicState === "loading" ? "Sende..." : "Magic Link senden"}
                            </Button>
                          </form>
                        )}
                      </>
                    )}
                  </TabsContent>

                  {/* Register Tab */}
                  <TabsContent value="register" className="space-y-4">
                    {regSuccess ? (
                      <div className="space-y-4">
                        <div className="flex items-start gap-3 p-4 rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800">
                          <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                          <div className="text-sm text-green-700 dark:text-green-300">
                            <p className="font-medium">Konto erstellt!</p>
                            <p>Wir haben Ihnen eine E-Mail zur Bestätigung gesendet. Bitte prüfen Sie Ihr Postfach und klicken Sie auf den Bestätigungs-Link.</p>
                          </div>
                        </div>
                        <p className="text-xs text-center text-muted-foreground">
                          Keine E-Mail erhalten? Bitte prüfen Sie Ihren Spam-Ordner.
                        </p>
                      </div>
                    ) : (
                      <form onSubmit={handleRegister} className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-2">
                            <Label htmlFor="reg-firstname">Vorname</Label>
                            <div className="relative">
                              <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                              <Input
                                id="reg-firstname"
                                placeholder="Max"
                                className="pl-9"
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                                required
                              />
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="reg-lastname">Nachname</Label>
                            <Input
                              id="reg-lastname"
                              placeholder="Mustermann"
                              value={lastName}
                              onChange={(e) => setLastName(e.target.value)}
                              required
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="reg-email">E-Mail</Label>
                          <div className="relative">
                            <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                            <Input
                              id="reg-email"
                              type="email"
                              placeholder="max@unternehmen.de"
                              className="pl-9"
                              value={regEmail}
                              onChange={(e) => setRegEmail(e.target.value)}
                              required
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="reg-password">Passwort</Label>
                          <div className="relative">
                            <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                            <Input
                              id="reg-password"
                              type="password"
                              placeholder="Min. 8 Zeichen, 1 Großbuchstabe, 1 Zahl"
                              className="pl-9"
                              value={regPassword}
                              onChange={(e) => setRegPassword(e.target.value)}
                              required
                              minLength={8}
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="reg-confirm">Passwort bestätigen</Label>
                          <div className="relative">
                            <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                            <Input
                              id="reg-confirm"
                              type="password"
                              placeholder="Passwort wiederholen"
                              className="pl-9"
                              value={regConfirm}
                              onChange={(e) => setRegConfirm(e.target.value)}
                              required
                            />
                          </div>
                        </div>

                        {regError && (
                          <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
                            <AlertCircle className="h-4 w-4 flex-shrink-0" />
                            {regError}
                          </div>
                        )}

                        <Button type="submit" className="w-full" disabled={regState === "loading"}>
                          {regState === "loading" ? "Registrieren..." : "Konto erstellen"}
                        </Button>
                      </form>
                    )}
                  </TabsContent>
                </Tabs>

                <p className="text-xs text-center text-muted-foreground pt-2">
                  Mit der Anmeldung akzeptieren Sie unsere{" "}
                  <Link href="/datenschutz" className="underline hover:text-primary">
                    Datenschutzrichtlinien
                  </Link>
                </p>
              </CardContent>
            </FloatingCard>
          </AnimatedSection>
        </div>
      </main>

      <footer className="border-t border-border/40 py-6">
        <div className="container px-6 lg:px-8">
          <div className="flex flex-col gap-4 items-center">
            <div className="flex items-center space-x-2">
              <div className="w-5 h-5 rounded-md bg-primary flex items-center justify-center">
                <span className="text-primary-foreground font-bold text-xs font-playfair">A</span>
              </div>
              <span className="font-semibold text-primary font-playfair text-sm">Social Media A-Team</span>
            </div>
            <div className="flex flex-wrap justify-center gap-3 text-xs text-muted-foreground">
              <span>&copy; 2025 SumoBrands</span>
              <Link href="/impressum" className="hover:text-primary hover:underline">
                Impressum
              </Link>
              <Link href="/datenschutz" className="hover:text-primary hover:underline">
                Datenschutz
              </Link>
              <Link href="/agb" className="hover:text-primary hover:underline">
                AGB
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
